import { useState, useRef, useEffect, useCallback, memo } from 'react';
import type { ChatMessage } from '@/stores/chat.store';
import type { MessageAttachment } from '@goodchat/shared/types';
import { useAuthStore } from '@/stores/auth.store';
import { usePresenceStore } from '@/stores/presence.store';
import { useStatusStore, isCurrentUserUnavailable } from '@/stores/status.store';
import { toast } from '@/stores/toast.store';
import { VoiceMemoPlayer } from './voice-memo-player';
import { AcknowledgmentBar } from './acknowledgment-bar';
import { LinkPreviewCard } from './link-preview-card';
import { ProgressiveImage } from './progressive-image';
import { MarkdownContent } from './markdown-content';
import { PollCard } from './poll-card';
import { CommentCard } from './comment-card';
import { EmojiPickerPopover } from './emoji-picker';
import { Tooltip } from '@/components/ui/tooltip';
import { getSmartSnoozePresets } from '@/lib/reply-later-presets';

interface MessageItemProps {
  message: ChatMessage;
  onEdit: (messageId: string, content: string) => void;
  onDelete: (messageId: string) => void;
  onReply: (message: ChatMessage) => void;
  onRetry?: (message: ChatMessage) => void;
  onMediaClick?: (attachment: MessageAttachment) => void;
  onReact?: (messageId: string, emoji: string) => void;
  onVoicePlaybackEnd?: (messageId: string) => void;
  voiceAutoPlay?: boolean;
  onAcknowledge?: (messageId: string) => void;
  onViewReceipts?: (messageId: string) => void;
  onFlagReplyLater?: (messageId: string) => void;
  onReplyLaterWithSnooze?: (messageId: string, snoozedUntil: string) => void;
  onPin?: (messageId: string) => void;
  onBookmark?: (messageId: string) => void;
  onHideLinkPreview?: (messageId: string) => void;
  onViewThread?: (threadId: string) => void;
  canPin?: boolean;
  isAdmin?: boolean;
  isReplyLater?: boolean;
  isGrouped?: boolean;
  viewStyle?: 'classic' | 'bubble';
  orgSlug?: string;
  areaSlug?: string;
  customEmojiMap?: Map<string, string>;
  customEmoji?: import('@goodchat/shared/types').CustomEmoji[];
}

export const MessageItem = memo(function MessageItem({ message, onEdit, onDelete, onReply, onRetry, onMediaClick, onReact, onVoicePlaybackEnd, voiceAutoPlay, onAcknowledge, onViewReceipts, onFlagReplyLater, onReplyLaterWithSnooze, onPin, onBookmark, onHideLinkPreview, onViewThread, canPin, isAdmin, isReplyLater, isGrouped, viewStyle = 'classic', orgSlug, areaSlug, customEmojiMap, customEmoji }: MessageItemProps) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const authorAvailability = useStatusStore((s) => s.availabilityByUserId[message.authorId]);
  const myNotificationSettings = useStatusStore((s) => s.myNotificationSettings);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content ?? '');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const snoozeRef = useRef<HTMLDivElement>(null);

  const isAuthor = message.authorId === currentUserId;
  const isAuthorOnline = usePresenceStore((s) => s.onlineUserIds.has(message.authorId));

  // Determine if the author is unavailable (DND or outside working hours)
  const isAuthorUnavailable = isAuthor
    ? isCurrentUserUnavailable(myNotificationSettings)
    : !!(authorAvailability?.isDnd || authorAvailability?.isOutsideWorkingHours);
  const messageDate = new Date(message.createdAt);
  const isOlderThan24h = Date.now() - messageDate.getTime() > 24 * 60 * 60 * 1000;
  const time = isOlderThan24h
    ? messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ', ' +
      messageDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : messageDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const fullDateTime = messageDate.toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  // Close emoji picker on outside click
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEmojiPicker]);

  // Close snooze menu on outside click
  useEffect(() => {
    if (!showSnoozeMenu) return;
    const handler = (e: MouseEvent) => {
      if (snoozeRef.current && !snoozeRef.current.contains(e.target as Node)) {
        setShowSnoozeMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSnoozeMenu]);

  const isBubble = viewStyle === 'bubble';

  if (message.isDeleted) {
    return (
      <div className={`group flex gap-3 px-4 py-1.5 opacity-50 ${viewStyle === 'classic' ? 'border-b border-gray-100 dark:border-gray-800' : ''}`}>
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          {message.author.displayName.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-gray-400 dark:text-gray-500">
              {message.author.displayName}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-600" title={fullDateTime}>{time}</span>
          </div>
          <p className="mt-0.5 text-sm italic text-gray-400 dark:text-gray-500">
            This message was deleted.
          </p>
        </div>
      </div>
    );
  }

  const handleSaveEdit = () => {
    if (editContent.trim() && editContent !== message.content) {
      onEdit(message.id, editContent.trim());
    }
    setIsEditing(false);
  };

  const attachments = message.attachments ?? [];
  const reactions = message.reactions ?? [];
  const isAnnouncement = message.messageType === 'announcement' || message.requiresAcknowledgment;

  // --- Shared sub-elements ---

  const avatarEl = (
    <div className="relative flex-shrink-0">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
        {message.author.profilePictureUrl ? (
          <img
            src={message.author.profilePictureUrl}
            alt=""
            className="h-9 w-9 rounded-full object-cover"
          />
        ) : (
          message.author.displayName.charAt(0).toUpperCase()
        )}
      </div>
      {isAuthorUnavailable ? (
        <span
          className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] leading-none dark:bg-gray-900"
          title="Do Not Disturb / Outside working hours"
          aria-label="User is in Do Not Disturb mode or outside working hours"
        >
          🌙
        </span>
      ) : isAuthorOnline ? (
        <span
          className="absolute -bottom-0.5 -right-0.5 flex h-2 w-2 rounded-full border-[1.5px] border-white bg-green-500 dark:border-gray-900"
          title="Online"
          aria-label="User is online"
        />
      ) : null}
    </div>
  );

  const metaBadges = (
    <>
      {message.author.isExternal && (
        <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          External
        </span>
      )}
      {isAnnouncement && (
        <span className="inline-flex items-center gap-1 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:bg-amber-800 dark:text-amber-200">
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
          Announcement
        </span>
      )}
      {message.isPinned && (
        <svg className="h-3 w-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.828.722a.5.5 0 01.354 0l.5.2A.5.5 0 0111 1.38V6a1 1 0 001 1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 00-1 1v4.618a.5.5 0 01-.318.458l-.5.2a.5.5 0 01-.354 0l-.5-.2A.5.5 0 019 15.618V11a1 1 0 00-1-1H6a1 1 0 01-1-1V7a1 1 0 011-1h2a1 1 0 001-1V1.382a.5.5 0 01.318-.458l.5-.2z" />
        </svg>
      )}
      {message.editedAt && (
        <span className="text-xs text-gray-400 dark:text-gray-500">(edited)</span>
      )}
      {isReplyLater && (
        <svg className="h-3 w-3 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-label="Flagged for reply later">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )}
      {message.isPending && (
        <span className="text-xs text-gray-400">Sending...</span>
      )}
      {message.isFailed && (
        <span className="text-xs text-red-500">Failed to send</span>
      )}
    </>
  );

  const replyPreviewEl = message.replyTo && (
    <div className="mt-1 flex items-center gap-1 rounded border-l-2 border-blue-400 bg-gray-100 px-2 py-1 dark:bg-gray-800">
      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
        {message.replyTo.authorDisplayName}
      </span>
      <span className="truncate text-xs text-gray-600 dark:text-gray-300">
        {message.replyTo.isDeleted
          ? 'This message was deleted.'
          : message.replyTo.content}
      </span>
    </div>
  );

  const editingEl = isEditing && (
    <div className="mt-1">
      <textarea
        value={editContent}
        onChange={(e) => setEditContent(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSaveEdit();
          }
          if (e.key === 'Escape') setIsEditing(false);
        }}
        className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        rows={2}
        autoFocus
      />
      <div className="mt-1 flex gap-2 text-xs">
        <button
          onClick={handleSaveEdit}
          className="text-blue-600 hover:underline dark:text-blue-400"
        >
          Save
        </button>
        <button
          onClick={() => setIsEditing(false)}
          className="text-gray-600 hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // Thread card (DESIGN.md move #4): threads referenced in chat render as a
  // distinct bordered card — kind label, glyph chip, "Open thread →" — never
  // as plain message text. Announcements get the amber semantic variant.
  const linkedThreadEl = message.linkedThreadId && onViewThread && (
    <button
      onClick={() => onViewThread(message.linkedThreadId!)}
      className="group mt-2 flex w-full max-w-[520px] items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-left shadow-sm transition-colors hover:border-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-blue-400"
    >
      <span
        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${
          isAnnouncement
            ? 'bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400'
            : 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
        }`}
      >
        {isAnnouncement ? (
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
        ) : (
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <span
          className={`block text-[10px] font-bold uppercase tracking-[0.07em] ${
            isAnnouncement ? 'text-amber-600 dark:text-amber-400' : 'text-blue-700 dark:text-blue-300'
          }`}
        >
          {isAnnouncement ? 'Announcement' : 'Thread'}
        </span>
        <p className="truncate text-sm text-gray-600 dark:text-gray-300">
          {isAnnouncement ? 'Open to discuss and acknowledge' : 'Open the discussion'}
        </p>
      </div>
      <span className="flex flex-shrink-0 items-center gap-1 text-xs font-semibold text-blue-700 dark:text-blue-300">
        Open thread
        <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </span>
    </button>
  );

  const attachmentsEl = attachments.length > 0 && (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((att) => (
        <AttachmentPreview
          key={att.id}
          attachment={att}
          onClick={() => onMediaClick?.(att)}
        />
      ))}
    </div>
  );

  const voiceMemoEl = message.voiceMemo && (
    <div className="mt-2">
      <VoiceMemoPlayer
        fileUrl={message.voiceMemo.fileUrl}
        durationSeconds={message.voiceMemo.durationSeconds}
        waveformData={message.voiceMemo.waveformData}
        transcript={message.voiceMemo.transcript}
        onPlaybackEnd={() => onVoicePlaybackEnd?.(message.id)}
        autoPlay={voiceAutoPlay}
      />
    </div>
  );

  const pollEl = message.messageType === 'poll' && (message as any).poll && (
    <PollCard
      messageId={message.id}
      poll={(message as any).poll}
      isAuthor={isAuthor}
    />
  );

  const reactionsEl = reactions.length > 0 && (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {reactions.map((r) => (
        <button
          key={r.emoji}
          onClick={() => onReact?.(message.id, r.emoji)}
          title={r.users.map((u) => u.displayName).join(', ')}
          aria-label={`${r.emoji} reaction, ${r.count} ${r.count === 1 ? 'person' : 'people'}${r.includesMe ? ', including you' : ''}`}
          /* Reaction pills are among the most-tapped things in a chat, and
             py-0.5 made them 22px — under WCAG 2.2 SC 2.5.8's 24px floor. */
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs transition-colors ${
            r.includesMe
              ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-300'
              : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700'
          }`}
        >
          {/^:.+:$/.test(r.emoji) && customEmojiMap?.has(r.emoji) ? (
            <img src={customEmojiMap.get(r.emoji)} alt={r.emoji} className="h-3.5 w-3.5 object-contain" />
          ) : (
            <span>{r.emoji}</span>
          )}
          <span>{r.count}</span>
        </button>
      ))}
    </div>
  );

  const ackBarEl = message.requiresAcknowledgment && message.acknowledgmentSummary && onAcknowledge && (
    <AcknowledgmentBar
      summary={message.acknowledgmentSummary}
      onAcknowledge={() => onAcknowledge(message.id)}
    />
  );

  // Inline comment threads (design v2). Internal ones are already filtered
  // server-side by the caller's membership, so anything present here is
  // something this user is allowed to see.
  const commentsEl = message.comments && message.comments.length > 0 && (
    <>
      {message.comments.map((summary) => (
        <CommentCard
          key={summary.threadId}
          areaId={message.areaId}
          messageId={message.id}
          summary={summary}
          currentUserId={currentUserId}
        />
      ))}
    </>
  );

  const linkPreviewsEl = message.linkPreviews && message.linkPreviews.length > 0 && !message.linkPreviewHidden && (
    <div className="mt-1 space-y-1">
      {message.linkPreviews.map((preview) => (
        <LinkPreviewCard
          key={preview.url}
          preview={preview}
          isAuthor={isAuthor}
          onHide={onHideLinkPreview ? () => onHideLinkPreview(message.id) : undefined}
        />
      ))}
    </div>
  );

  const retryEl = message.isFailed && onRetry && (
    <button
      onClick={() => onRetry(message)}
      className="mt-1 text-xs text-red-600 hover:underline dark:text-red-400"
    >
      Retry
    </button>
  );

  // Read receipt checkmarks: only for the current user's own sent messages
  const readReceiptEl = isAuthor && !message.isDeleted && !message.isPending && !message.isFailed && (
    <span
      className="inline-flex items-center"
      aria-label={
        (message.readCount ?? 0) > 0
          ? `Read by ${message.readCount} ${message.readCount === 1 ? 'person' : 'people'}`
          : 'Sent'
      }
      title={
        (message.readCount ?? 0) > 0
          ? `Read by ${message.readCount}`
          : 'Sent'
      }
    >
      {(message.readCount ?? 0) > 0 ? (
        // Double check — blue, indicating read by at least one person
        <svg className="h-3.5 w-3.5 text-blue-500" viewBox="0 0 24 16" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
          <polyline points="1.5 8.5 6 13 14 4" />
          <polyline points="8.5 8.5 13 13 21 4" />
        </svg>
      ) : (
        // Single check — gray, indicating sent but not yet read
        <svg className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
          <polyline points="2 8.5 6 12.5 14 4" />
        </svg>
      )}
    </span>
  );

  // --- Bubble layout ---
  if (isBubble) {
    return (
      <div
        role="article"
        aria-label={`Message from ${message.author.displayName} at ${time}`}
        className={`group relative px-4 hover:z-10 ${isGrouped ? 'py-0.5' : 'py-1'} ${message.isPending ? 'opacity-60' : ''} ${message.isFailed ? 'bg-red-50 dark:bg-red-950/20' : ''}`}
      >
        {/* Bubble row: avatar + bubble */}
        <div className={`flex items-start gap-2 ${isAuthor ? 'flex-row-reverse' : ''}`}>
          {isGrouped ? <div className="w-9 flex-shrink-0" /> : avatarEl}
          <div className="max-w-[75%]">
            <div className={`rounded-2xl px-4 py-2 ${
              isAuthor
                ? 'bg-blue-500 text-white [&_p]:text-white [&_ul]:text-white [&_ol]:text-white [&_li]:text-white [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_blockquote]:text-blue-100 [&_blockquote]:border-blue-300 [&_a]:text-blue-100 [&_a]:underline [&_code]:bg-blue-400/30 [&_code]:text-blue-50 [&_pre]:bg-blue-600/50 [&_.mention-highlight]:bg-blue-400/30 [&_.mention-highlight]:text-white'
                : `bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100 ${
                    isAnnouncement ? 'border-l-4 border-l-amber-400 bg-amber-50 dark:border-l-amber-600 dark:bg-amber-950/30' : ''
                  }`
            }`}>
              {replyPreviewEl}
              {isEditing ? editingEl : (
                message.content ? <MarkdownContent content={message.content} /> : null
              )}
              {linkedThreadEl}
              {attachmentsEl}
              {voiceMemoEl}
              {pollEl}
            </div>

            {/* Name + time + badges below the bubble */}
            {!isGrouped && (
              <div className={`mt-0.5 flex items-center gap-1.5 ${isAuthor ? 'flex-row-reverse' : ''}`}>
                <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                  {message.author.displayName}
                </span>
                <span className="text-[10px] text-gray-400 dark:text-gray-500" title={fullDateTime}>{time}</span>
                {readReceiptEl}
                {metaBadges}
              </div>
            )}

            {/* Action menu — below bubble, aligned to bubble side */}
            {!message.isPending && !message.isFailed && (
              <div className={`mt-0.5 flex gap-1 opacity-0 transition-opacity duration-100 group-hover:opacity-100 ${isAuthor ? 'flex-row-reverse' : ''}`}>
                <ActionMenuButtons
                  message={message}
                  isAuthor={isAuthor}
                  canPin={canPin}
                  isAdmin={isAdmin}
                  onReact={onReact}
                  onPin={onPin}
                  onBookmark={onBookmark}
                  onViewReceipts={onViewReceipts}
                  onFlagReplyLater={onFlagReplyLater}
                  onReplyLaterWithSnooze={onReplyLaterWithSnooze}
                  onReply={onReply}
                  onEdit={(content) => { setEditContent(content); setIsEditing(true); }}
                  onDelete={onDelete}
                  showEmojiPicker={showEmojiPicker}
                  setShowEmojiPicker={setShowEmojiPicker}
                  showSnoozeMenu={showSnoozeMenu}
                  setShowSnoozeMenu={setShowSnoozeMenu}
                  pickerRef={pickerRef}
                  snoozeRef={snoozeRef}
                  orgSlug={orgSlug}
                  areaSlug={areaSlug}
                  customEmoji={customEmoji}
                />
              </div>
            )}
          </div>
        </div>

        {/* Items below the bubble (reactions, ack bar, link previews) */}
        <div className={`mt-0.5 ${isAuthor ? 'flex flex-col items-end pr-11' : 'pl-11'}`}>
          {reactionsEl}
          {commentsEl}
          {ackBarEl}
          {linkPreviewsEl}
          {retryEl}
        </div>
      </div>
    );
  }

  // --- Classic layout ---
  return (
    <div
      role="article"
      aria-label={`Message from ${message.author.displayName} at ${time}`}
      className={`group relative flex gap-3 px-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
        isGrouped ? 'py-0.5' : 'border-b border-gray-100 py-1.5 dark:border-gray-800'
      } ${
        message.isPending ? 'opacity-60' : ''
      } ${message.isFailed ? 'bg-red-50 dark:bg-red-950/20' : ''} ${
        isAnnouncement ? 'border-l-4 border-l-amber-400 bg-amber-50 hover:bg-amber-100/80 dark:border-l-amber-600 dark:bg-amber-950/20 dark:hover:bg-amber-950/30' : ''
      }`}
    >
      {isGrouped ? <div className="w-9 flex-shrink-0" /> : avatarEl}
      <div className="min-w-0 flex-1">
        {!isGrouped && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {message.author.displayName}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500" title={fullDateTime}>{time}</span>
            {readReceiptEl}
            {metaBadges}
          </div>
        )}
        {replyPreviewEl}
        {isEditing ? editingEl : (
          message.content ? <MarkdownContent content={message.content} /> : null
        )}
        {linkedThreadEl}
        {attachmentsEl}
        {voiceMemoEl}
        {pollEl}
        {reactionsEl}
        {commentsEl}
        {ackBarEl}
        {linkPreviewsEl}
        {retryEl}
      </div>

      {/* Action menu */}
      {!message.isPending && !message.isFailed && (
        <div className="pointer-events-none absolute right-2 top-1 flex gap-1 opacity-0 transition-opacity duration-100 group-hover:pointer-events-auto group-hover:opacity-100 group-hover:delay-150">
          <ActionMenuButtons
            message={message}
            isAuthor={isAuthor}
            canPin={canPin}
            isAdmin={isAdmin}
            onReact={onReact}
            onPin={onPin}
            onBookmark={onBookmark}
            onViewReceipts={onViewReceipts}
            onFlagReplyLater={onFlagReplyLater}
            onReplyLaterWithSnooze={onReplyLaterWithSnooze}
            onReply={onReply}
            onEdit={(content) => { setEditContent(content); setIsEditing(true); }}
            onDelete={onDelete}
            showEmojiPicker={showEmojiPicker}
            setShowEmojiPicker={setShowEmojiPicker}
            showSnoozeMenu={showSnoozeMenu}
            setShowSnoozeMenu={setShowSnoozeMenu}
            pickerRef={pickerRef}
            snoozeRef={snoozeRef}
            orgSlug={orgSlug}
            areaSlug={areaSlug}
            customEmoji={customEmoji}
          />
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  return (
    prev.message.id === next.message.id &&
    prev.message.editedAt === next.message.editedAt &&
    prev.message.isDeleted === next.message.isDeleted &&
    prev.message.isPending === next.message.isPending &&
    prev.message.isFailed === next.message.isFailed &&
    prev.message.isPinned === next.message.isPinned &&
    prev.message.isBookmarked === next.message.isBookmarked &&
    prev.message.reactions === next.message.reactions &&
    prev.message.acknowledgmentSummary === next.message.acknowledgmentSummary &&
    prev.message.linkPreviews === next.message.linkPreviews &&
    prev.message.readCount === next.message.readCount &&
    // Comment summaries change by identity when a comment arrives over the
    // socket. Omitted, the store updated and this memo silently blocked the
    // re-render — the card kept showing a stale count with no error anywhere.
    prev.message.comments === next.message.comments &&
    prev.voiceAutoPlay === next.voiceAutoPlay &&
    prev.canPin === next.canPin &&
    prev.isAdmin === next.isAdmin &&
    prev.isReplyLater === next.isReplyLater &&
    prev.isGrouped === next.isGrouped &&
    prev.viewStyle === next.viewStyle &&
    prev.orgSlug === next.orgSlug &&
    prev.areaSlug === next.areaSlug
  );
})

function ActionMenuButtons({
  message,
  isAuthor,
  canPin,
  isAdmin,
  onReact,
  onPin,
  onBookmark,
  onViewReceipts,
  onFlagReplyLater,
  onReplyLaterWithSnooze,
  onReply,
  onEdit,
  onDelete,
  showEmojiPicker,
  setShowEmojiPicker,
  showSnoozeMenu,
  setShowSnoozeMenu,
  pickerRef,
  snoozeRef,
  orgSlug,
  areaSlug,
  customEmoji,
}: {
  message: ChatMessage;
  isAuthor: boolean;
  canPin?: boolean;
  isAdmin?: boolean;
  onReact?: (messageId: string, emoji: string) => void;
  onPin?: (messageId: string) => void;
  onBookmark?: (messageId: string) => void;
  onViewReceipts?: (messageId: string) => void;
  onFlagReplyLater?: (messageId: string) => void;
  onReplyLaterWithSnooze?: (messageId: string, snoozedUntil: string) => void;
  onReply: (message: ChatMessage) => void;
  onEdit: (content: string) => void;
  onDelete: (messageId: string) => void;
  showEmojiPicker: boolean;
  setShowEmojiPicker: (fn: (v: boolean) => boolean) => void;
  showSnoozeMenu: boolean;
  setShowSnoozeMenu: (fn: (v: boolean) => boolean) => void;
  pickerRef: React.RefObject<HTMLDivElement | null>;
  snoozeRef: React.RefObject<HTMLDivElement | null>;
  orgSlug?: string;
  areaSlug?: string;
  customEmoji?: import('@goodchat/shared/types').CustomEmoji[];
}) {
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyLink = () => {
    if (!orgSlug || !areaSlug) return;
    const url = `${window.location.origin}/org/${orgSlug}/${areaSlug}?msg=${message.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  return (
    <>
      {onReact && (
        <div className="relative" ref={pickerRef}>
          <Tooltip label="React">
            <button
              onClick={() => setShowEmojiPicker((v) => !v)}
              className="rounded p-1 text-gray-400 transition-transform duration-75 hover:bg-gray-200 hover:text-gray-600 active:scale-90 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              aria-label="React with emoji"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </Tooltip>
          {showEmojiPicker && (
            <EmojiPickerPopover
              onSelect={(emoji) => onReact(message.id, emoji)}
              onClose={() => setShowEmojiPicker(() => false)}
              customEmoji={customEmoji}
            />
          )}
        </div>
      )}
      {canPin && onPin && (
        <Tooltip label={message.isPinned ? 'Unpin' : 'Pin'}>
          <button
            onClick={() => onPin(message.id)}
            className={`rounded p-1 transition-transform duration-75 active:scale-90 ${
              message.isPinned
                ? 'text-yellow-500 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
                : 'text-gray-400 hover:bg-yellow-100 hover:text-yellow-600 dark:hover:bg-yellow-900/30 dark:hover:text-yellow-400'
            }`}
            aria-label={message.isPinned ? 'Unpin message' : 'Pin message'}
          >
            <svg className="h-4 w-4" fill={message.isPinned ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 17v5M9 10.76a2 2 0 01-1.11 1.79C7 13.26 7 14.38 7 16h10c0-1.62 0-2.74-.89-3.45A2 2 0 0115 10.76V5a1 1 0 00-1-1h-4a1 1 0 00-1 1v5.76z" />
            </svg>
          </button>
        </Tooltip>
      )}
      {onBookmark && (
        <Tooltip label={message.isBookmarked ? 'Unbookmark' : 'Bookmark'}>
          <button
            onClick={() => onBookmark(message.id)}
            className={`rounded p-1 transition-transform duration-75 active:scale-90 ${
              message.isBookmarked
                ? 'text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                : 'text-gray-400 hover:bg-blue-100 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400'
            }`}
            aria-label={message.isBookmarked ? 'Remove bookmark' : 'Bookmark message'}
          >
            <svg className="h-4 w-4" fill={message.isBookmarked ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          </button>
        </Tooltip>
      )}
      {orgSlug && areaSlug && (
        <Tooltip label={linkCopied ? 'Copied!' : 'Copy link'}>
          <button
            onClick={handleCopyLink}
            className={`rounded p-1 transition-transform duration-75 active:scale-90 ${
              linkCopied
                ? 'text-green-500 hover:bg-green-100 dark:hover:bg-green-900/30'
                : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300'
            }`}
            aria-label="Copy link to message"
          >
            {linkCopied ? (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            )}
          </button>
        </Tooltip>
      )}
      {isAdmin && onViewReceipts && (
        <Tooltip label="View details">
          <button
            onClick={() => onViewReceipts(message.id)}
            className="rounded p-1 text-gray-400 transition-transform duration-75 hover:bg-gray-200 hover:text-gray-600 active:scale-90 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            aria-label="View read receipts"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </Tooltip>
      )}
      {onFlagReplyLater && (
        <div className="relative" ref={snoozeRef}>
          <Tooltip label="Reply later">
            <button
              onClick={() => setShowSnoozeMenu((v) => !v)}
              className="rounded p-1 text-gray-400 transition-transform duration-75 hover:bg-purple-100 hover:text-purple-600 active:scale-90 dark:hover:bg-purple-900/30 dark:hover:text-purple-400"
              aria-label="Reply later"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </Tooltip>
          {showSnoozeMenu && (
            <div className="absolute right-0 top-8 z-50 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
              <button
                onClick={() => {
                  onFlagReplyLater(message.id);
                  setShowSnoozeMenu(() => false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                </svg>
                Flag (no reminder)
              </button>
              <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
              {getSmartSnoozePresets().map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => {
                    if (onReplyLaterWithSnooze) {
                      onReplyLaterWithSnooze(message.id, preset.getDate().toISOString());
                    }
                    setShowSnoozeMenu(() => false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <Tooltip label="Reply">
        <button
          onClick={() => onReply(message)}
          className="rounded p-1 text-gray-400 transition-transform duration-75 hover:bg-gray-200 hover:text-gray-600 active:scale-90 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          aria-label="Reply to message"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </button>
      </Tooltip>
      {isAuthor && (
        <>
          <Tooltip label="Edit">
            <button
              onClick={() => onEdit(message.content ?? '')}
              className="rounded p-1 text-gray-400 transition-transform duration-75 hover:bg-gray-200 hover:text-gray-600 active:scale-90 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              aria-label="Edit message"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </Tooltip>
          <Tooltip label="Delete">
            <button
              onClick={() => onDelete(message.id)}
              className="rounded p-1 text-gray-400 transition-transform duration-75 hover:bg-red-100 hover:text-red-600 active:scale-90 dark:hover:bg-red-900/30 dark:hover:text-red-400"
              aria-label="Delete message"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </Tooltip>
        </>
      )}
    </>
  );
}

function AttachmentPreview({
  attachment,
  onClick,
}: {
  attachment: MessageAttachment;
  onClick: () => void;
}) {
  const isImage = attachment.fileType.startsWith('image/');
  const isVideo = attachment.fileType.startsWith('video/');
  const isAudio = attachment.fileType.startsWith('audio/');

  if (isImage) {
    if (attachment.thumbnailUrl && attachment.fileUrl) {
      return (
        <ProgressiveImage
          thumbnailUrl={attachment.thumbnailUrl}
          fullUrl={attachment.fileUrl}
          alt={attachment.fileName ?? ''}
          className="max-h-[300px] max-w-[300px] rounded-lg"
          onClick={onClick}
        />
      );
    }
    return (
      <button
        onClick={onClick}
        className="block overflow-hidden rounded-lg transition-opacity hover:opacity-90"
      >
        <img
          src={attachment.thumbnailUrl ?? attachment.fileUrl}
          alt={attachment.fileName ?? ''}
          className="max-h-[300px] max-w-[300px] rounded-lg object-cover"
          loading="lazy"
          decoding="async"
        />
      </button>
    );
  }

  if (isVideo) {
    return (
      <div className="max-w-full overflow-hidden rounded-lg sm:max-w-[400px]">
        <video
          src={attachment.fileUrl}
          controls
          preload="metadata"
          poster={attachment.thumbnailUrl ?? undefined}
          className="max-h-[300px] w-full rounded-lg"
        />
      </div>
    );
  }

  if (isAudio) {
    return (
      <div className="w-full max-w-full sm:max-w-[400px]">
        <audio src={attachment.fileUrl} controls preload="metadata" className="w-full" />
        {attachment.fileName && (
          <p className="mt-1 truncate text-xs text-gray-600 dark:text-gray-300">
            {attachment.fileName}
          </p>
        )}
      </div>
    );
  }

  // Generic file
  return (
    <a
      href={attachment.fileUrl}
      download={attachment.fileName ?? 'download'}
      className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
    >
      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
      <span className="truncate">{attachment.fileName ?? 'File'}</span>
    </a>
  );
}
